/**
 * @module app.core
 */

import { observable, autoinject } from 'aurelia-framework';
import { AssessmentContext } from 'app/core/lib/assessment-context';
import { IQuestionComponent, IQuestionConfig } from 'app/core/components/assessments/interfaces';
import { AppLogger } from 'app/shared/lib/logging/app-logger';
import { QuestionBase } from 'app/core/components/assessments/questions/question-base';
import { FormatProvider } from 'app/shared/lib/formatting/format-provider';
import * as Core from 'app/core/models/essette-api';
import { isNullOrUndefined } from 'app/shared/lib/utilities';


@autoinject
export class QuestionBmiCustomElement extends QuestionBase implements IQuestionComponent {

    ////****************** Fields and Properties **************************************////
    @observable({ changeHandler: 'calculateBmi' })
    protected feet: number | null = null;

    @observable({ changeHandler: 'calculateBmi' })
    protected inches: number | null = null;

    @observable({ changeHandler: 'calculateBmi' })
    protected weight: number | null = null;

    protected bmi: string | null = null;

    protected isNullOrUndefined = isNullOrUndefined;

    ////****************** Lifecycle events (in order of execution) ******************////
    constructor(
        protected readonly context: AssessmentContext,
        private readonly formatProvider: FormatProvider,
        private readonly logger: AppLogger) {

        super(context);
        this.logger.source = this;

        if(!context || !context.currentSession) {
            throw new Error("No Assessment context is available");
        }

        if(!this.context.questionComponents.includes(this)) {
            this.context.questionComponents.push(this);
        }
    }

    protected async activate(config: IQuestionConfig): Promise<void> {
        this.question = config.question;
        this.required = !!this.question.flag?.isRequired;
        this.showHistory = config.showHistory;
        this.readonly = config.readonly;
        this.isValid = !this.required;
        this.labelId = config.labelId;
    }

    protected attached(): void {
        this.loadPreviousAnswer();
    }


    ////****************** Events *******************************************************////
    private handleHeightChange(feetOrInch: string): void {
        if(feetOrInch === 'feet') {
            if(this.inches === null) this.inches = 0;
        } else {
            if(this.feet === null) this.feet = 0;
        }
    }

    ////****************** Functions ****************************************************////
    private async loadPreviousAnswer(): Promise<void> {
        const questionId = this.question && this.question.id;
        if(!questionId || !this.context.attachment.currentAnswers) { return; }

        const description = this.question && this.question.description || '';

        try {
            const answer = this.context.attachment.currentAnswers.find(a => {
                if(!a.question) { return false; }

                if(!this.question.sharedQuestion) {
                    return a.question.id === this.question.id;
                } else if(!!this.question.sharedQuestion && !!a.sharedQuestion) {
                    return this.question.sharedQuestion.id === a.sharedQuestion.id;
                }

                return false;

            });

            if(answer) {
                this.parseAnswerText(answer.value || '');
            }
        }
        catch(error) {
            this.logger.error(`Find existing answer error: ${error}`);
            this.logger.error(`Unable to find existing answer(s) for question ${questionId} (${description})`);
            throw error;
        }
    }

    private parseAnswerText(answerText: string): void {
        const pieces = answerText.split(', ');
        if(pieces.length !== 4) {
            this.logger.error(`Answer text does not contain expected number of segments (4)`);
            throw new Error('Answer text is invalid');
        }

        try {
            this.bmi = pieces[0];
            this.feet = this.formatProvider.parseNumber(pieces[1]);
            this.inches = this.formatProvider.parseNumber(pieces[2]);
            this.weight = this.formatProvider.parseNumber(pieces[3]);
        }
        catch(error) {
            throw new Error('Unable to parse values');
        }
    }

    protected calculateBmi(): void {
        // Answer text format: {bmi}, {feet}, {inches}, {weight}
        // Formula: https://www.thecalculatorsite.com/articles/health/bmi-formula-for-bmi-calculations.php

        //0 in feet or inches evaluates to not valid, check its not null or undefined, weight of 0 isn't valid
        if(!(!isNullOrUndefined(this.feet) && !isNullOrUndefined(this.inches) && this.weight && (this.feet > 0 || this.inches > 0))) {
            this.bmi = ' - ';
            return;
        }
        if(isNaN(this.feet) || isNaN(this.inches) || isNaN(this.weight)) { throw new Error("Some BMI values are not valid"); }

        const totalInches = (this.feet * 12) + this.inches;
        const bmi = (this.weight / (totalInches * totalInches)) * 703;
        this.bmi = this.formatProvider.formatDecimal(bmi, { format: 'fixed2' });
    }


    ////****************** IQuestionComponent ****************************************************////
    public saveAnswer = null;  // Not Implemented

    public getAnswers(): Core.AssessmentAnswer[] {
        const bmi = this.formatProvider.parseNumber(this.bmi);
        if(this.question && bmi && !isNaN(bmi)) {
            const answer: Core.AssessmentAnswer = {
                question: { id: this.question.id || null },
                text: `${bmi}, ${this.feet}, ${this.inches}, ${this.weight}`
            };

            return [answer];
        }

        return [];
    }

}
